import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/toast";
import { usePermission } from "@/hooks/use-permission";
import { fetchAdminGroups, fetchAdminUsers, type AdminUser } from "@/lib/api";
import type { UserGroup } from "@/types";
import { usePlainErrorMessage } from "@/components/data-display/api-failure";
import { classifyFailure, legacyErrorReason } from "@/lib/api-failure";
import {
  hasPermission,
  roleDisplayLabel,
  type AppRole,
} from "@/lib/permissions";

function initials(name: string) {
  return name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
}

/**
 * How many members a group has, or `null` when that is not known.
 *
 * `GET /v1/admin/groups` reports a `memberCount` per group but no member ids —
 * the id list is a separate route (`listAdminGroupMembers`) this page does not
 * call — so `memberIds` is empty for every summary. Counting only `memberIds`
 * therefore rendered "0 members" for every group the backend said had members:
 * prefer loaded ids when available, then the API's own count, and return `null` rather than a
 * zero when neither is known.
 */
function groupMemberCount(group: UserGroup): number | null {
  if (group.memberIds.length > 0) return group.memberIds.length;
  return typeof group.memberCount === "number" ? group.memberCount : null;
}

function memberCountLabel(group: UserGroup): string {
  const count = groupMemberCount(group);
  if (count === null) return "Member count unavailable";
  return `${count} member${count === 1 ? "" : "s"}`;
}

/**
 * The roles that grant the `group:read` permission (ONB-109), named in a denial.
 *
 * Derived from the permission table in `lib/permissions.ts` — the module's own
 * header calls it the single source of truth for RBAC — so a role gaining or
 * losing `group:read` moves this copy with it. The generic organization admin is
 * excluded by construction: the sentence's second half already names it, and a
 * viewer reading a denial by definition does not hold it. Labels come from
 * `roleDisplayLabel`, so the reader sees "Security Admin", never the raw
 * `security-admin` id or the `auth.admin_users.read` permission key.
 */
const GROUP_READ_ROLE_KEYS: AppRole[] = [
  "security-admin",
  "developer",
  "wallet-admin",
  "wallet_operator",
  "compliance-officer",
  "policy_approver",
  "treasury-operator",
  "finance_operator",
  "finance_viewer",
  "auditor",
  "support-readonly",
];

function groupReadRoleNames(): string {
  const labels = GROUP_READ_ROLE_KEYS.filter((role) => hasPermission(role, "group:read")).map((role) =>
    roleDisplayLabel(role),
  );
  if (labels.length === 0) return "Organization Admin";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}

interface GroupMember {
  id: string;
  name: string;
}

/** Local capability refusal, never a server receipt or a saved membership. */
type GroupWriteFailure = {
  reasonCode: "GROUP_WRITE_UNAVAILABLE";
  operation: "create" | "add-member";
};

function GroupWriteRefusal({ failure }: { failure: GroupWriteFailure }) {
  return (
    <div role="alert" data-reason-code={failure.reasonCode} className="rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger">
      <p className="font-semibold">Group changes unavailable</p>
      <p className="mt-1">No group or membership changes were saved.</p>
    </div>
  );
}

export function GroupManagement() {
  const plainError = usePlainErrorMessage();
  const canManage = usePermission("admin:access");
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<GroupMember[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [manageId, setManageId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newMemberQuery, setNewMemberQuery] = useState("");
  const [newMembers, setNewMembers] = useState<string[]>([]);
  const [manageMemberQuery, setManageMemberQuery] = useState("");
  const [writeFailure, setWriteFailure] = useState<GroupWriteFailure | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAdminGroups()
      .then((result) => {
        if (cancelled) return;
        if (result.error) {
          setError(legacyErrorReason(result.error));
        } else {
          setGroups(result.groups.map((g) => ({
            id: g.groupId,
            name: g.name,
            description: g.description || "No description provided.",
            memberIds: [],
            memberCount: typeof g.memberCount === "number" ? g.memberCount : null,
            permissions: [],
          })));
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("NETWORK_ERROR");
        setLoading(false);
      });
    fetchAdminUsers().then((result) => {
      if (cancelled) return;
      if (!result.error) {
        setAllUsers(result.users.map((u: AdminUser) => ({
          id: u.principalId,
          name: u.displayName || u.email,
        })));
      }
    });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(
    () => groups.filter((g) => !query || g.name.toLowerCase().includes(query.toLowerCase())),
    [groups, query]
  );

  const manageGroup = groups.find((g) => g.id === manageId) ?? null;

  function findUser(id: string): GroupMember | undefined {
    return allUsers.find((u) => u.id === id);
  }

  function resetCreateForm() {
    setNewName("");
    setNewDesc("");
    setNewMemberQuery("");
    setNewMembers([]);
  }

  function handleCreate() {
    if (!newName.trim()) return;
    setWriteFailure({ reasonCode: "GROUP_WRITE_UNAVAILABLE", operation: "create" });
  }

  function addNewMember() {
    const q = newMemberQuery.trim().toLowerCase();
    if (!q) return;
    const user = allUsers.find((u) => u.name.toLowerCase().includes(q) || u.id.toLowerCase() === q);
    if (!user) {
      toast({ variant: "info", title: "No matching user found", description: `No user matches "${newMemberQuery}".` });
      return;
    }
    if (!newMembers.includes(user.id)) setNewMembers((m) => [...m, user.id]);
    setNewMemberQuery("");
  }

  function addManageMember() {
    if (!manageGroup || !manageMemberQuery.trim()) return;
    setWriteFailure({ reasonCode: "GROUP_WRITE_UNAVAILABLE", operation: "add-member" });
  }

  return (
    <div>
      <nav className="mb-5 flex items-center gap-1.5 text-[13px]" aria-label="Breadcrumb">
        <Link to="/admin/users" className="text-primary no-underline hover:underline">Identity</Link>
        <span className="text-muted" aria-hidden>›</span>
        <span className="font-medium text-text">Groups</span>
      </nav>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold text-ink">Group Management</h1>
          <p className="mt-1 text-sm text-muted">Organize users into groups with shared permissions.</p>
        </div>
      </div>

      {error && !loading && (
        <div
          role="alert"
          data-testid="groups-load-error"
          className="mb-4 rounded-lg border border-danger bg-danger-soft px-4 py-3 text-sm text-danger"
        >
          {/* ONB-109: this used to add a mono `Code: HTTP_500` line under the
              sentence. The gateway's code is a machine handle an administrator
              cannot act on (ALL-5774 / D636): removed, and `fetchAdminGroups`
              mints no support reference for this response, so there is no
              reference to show here. A revision that returns one must render it
              through the shared SupportDetails component, never as a raw line.
              A 403 is a denial, not a failure — naming the roles that grant
              `group:read` (the way features/wallets/wallet-request.tsx does) is
              the actionable sentence; anything else keeps the plain one. */}
          {classifyFailure({ reasonCode: error }) === "denied" ? (
            <>Viewing groups needs the {groupReadRoleNames()} role. Ask an organization admin to assign it.</>
          ) : (
            <>Couldn&apos;t load groups. {plainError({ reasonCode: error })}</>
          )}
        </div>
      )}

      {loading && (
        <div className="mb-4 text-center text-sm text-muted">Loading groups...</div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        <div className="relative w-[280px] max-w-full">
          <Search aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search groups..."
            className="h-[36px] w-full rounded-[8px] border border-line bg-surface pl-9 pr-3 text-[13px] text-ink outline-none transition-colors placeholder:text-subtle focus:border-primary focus:shadow-[0_0_0_3px_var(--ads-primary-soft)]"
          />
        </div>
        {canManage && (
          <Button className="ml-auto gap-1" onClick={() => { setWriteFailure(null); setCreateOpen(true); }}>
            <Plus className="size-4" aria-hidden /> Create Group
          </Button>
        )}
      </div>

      {!loading && !error && filtered.length === 0 ? (
        <div className="rounded-[10px] border border-line bg-surface px-4 py-10 text-center text-sm text-muted">
          {query ? "No groups match your search." : "No groups found."}
        </div>
      ) : !loading && !error && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((g) => (
            <div key={g.id} className="rounded-[10px] border border-line bg-surface p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-ink">{g.name}</div>
                {canManage && (
                  <button type="button" className="text-xs font-semibold text-primary hover:underline" onClick={() => { setWriteFailure(null); setManageId(g.id); }}>
                    Manage
                  </button>
                )}
              </div>
              <div className="mt-1 text-xs text-muted">{memberCountLabel(g)}</div>
              <div className="mt-2 text-[11.5px] leading-relaxed text-muted">
                {g.description}
              </div>
              <div className="mt-3 flex -space-x-2">
                {g.memberIds.slice(0, 5).map((id) => {
                  const u = findUser(id);
                  return u ? (
                    <div key={id} className="rounded-full ring-2 ring-surface" title={u.name}>
                      <Avatar initials={initials(u.name)} size={28} fontSize={11} />
                    </div>
                  ) : null;
                })}
                {g.memberIds.length > 5 && (
                  <div className="grid size-7 place-items-center rounded-full bg-bg text-[10px] font-semibold text-muted ring-2 ring-surface">
                    +{g.memberIds.length - 5}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={createOpen}
        onOpenChange={(v) => {
          setCreateOpen(v);
          if (!v) { resetCreateForm(); setWriteFailure(null); }
        }}
        title="Create Group"
        description="Review a group name and proposed members."
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button disabled={!newName.trim()} onClick={handleCreate}>Create Group</Button>
          </>
        }
      >
        <div className="space-y-4">
          {writeFailure?.operation === "create" && <GroupWriteRefusal failure={writeFailure} />}
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="grp-name">Group Name</label>
            <input
              id="grp-name"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Risk Management"
              className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="grp-desc">Description</label>
            <input
              id="grp-desc"
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Purpose of this group..."
              className="h-[36px] w-full rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-ink" htmlFor="grp-member">Add Members</label>
            <div className="flex gap-2">
              <input
                id="grp-member"
                type="text"
                value={newMemberQuery}
                onChange={(e) => setNewMemberQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNewMember(); } }}
                placeholder="Search by name or user id..."
                className="h-[36px] flex-1 rounded-[6px] border border-line-strong bg-surface px-3 text-sm text-ink outline-none focus:border-primary"
              />
              <Button type="button" variant="secondary" onClick={addNewMember}>Add</Button>
            </div>
            {newMembers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {newMembers.map((id) => {
                  const u = findUser(id);
                  return (
                    <Badge key={id} variant="accent" dot={false} className="gap-1">
                      {u?.name ?? id}
                      <button type="button" aria-label={`Remove ${u?.name ?? id}`} onClick={() => setNewMembers((m) => m.filter((mid) => mid !== id))}>
                        <X className="size-3" aria-hidden />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Drawer>

      <Drawer
        open={!!manageGroup}
        onOpenChange={(v) => { if (!v) { setManageId(null); setWriteFailure(null); } }}
        title={manageGroup?.name ?? ""}
        description="Review group details and reported membership."
      >
        {manageGroup && (
          <div className="space-y-5">
            {writeFailure && writeFailure.operation !== "create" && <GroupWriteRefusal failure={writeFailure} />}
            <p className="text-sm text-muted">{manageGroup.description}</p>
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Permissions</h4>
              {manageGroup.permissions.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {manageGroup.permissions.map((p) => (
                    <Badge key={p} variant="neutral" dot={false}>{p}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted">Permissions not reported.</p>
              )}
            </div>
            <div>
              <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Members ({groupMemberCount(manageGroup) ?? "—"})</h4>
              {manageGroup.memberIds.length === 0 && <p className="mb-2 text-sm text-muted">Member list not reported.</p>}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manageMemberQuery}
                  onChange={(e) => setManageMemberQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addManageMember(); } }}
                  placeholder="Search by name or user id..."
                  className="h-[32px] flex-1 rounded-[6px] border border-line-strong bg-surface px-3 text-xs text-ink outline-none focus:border-primary"
                />
                <Button type="button" size="sm" variant="secondary" onClick={addManageMember}>Add</Button>
              </div>
              {/* No member-ids read is wired (the summary maps memberIds to []), so
                  there is no member list and no Remove control to refuse. A
                  revision that reads member ids adds both back, with a case. */}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
